import { createSignal, createEffect, on, onMount, For, Show, createMemo } from "solid-js";
import { api, type AuthSessionInfo } from "../shared";

const PAGE_SIZE = 20;

export function AuthSessionsTable(props: { organizationId?: string }) {
  const [sessions, setSessions] = createSignal<AuthSessionInfo[]>([]);
  const [total, setTotal] = createSignal(0);
  const [page, setPage] = createSignal(1);
  const [status, setStatus] = createSignal<"active" | "expired" | "all">("active");
  const [revokeConfirm, setRevokeConfirm] = createSignal<{ id: string; hasRefreshToken: boolean } | null>(null);
  const [message, setMessage] = createSignal("");
  const [error, setError] = createSignal("");

  const totalPages = createMemo(() => Math.max(1, Math.ceil(total() / PAGE_SIZE)));

  async function loadSessions(p?: number) {
    const targetPage = p ?? page();
    const offset = (targetPage - 1) * PAGE_SIZE;
    try {
      const res = await api.sessions.authList({ status: status(), limit: PAGE_SIZE, offset, organizationId: props.organizationId });
      setSessions(res.items || []);
      setTotal(res.total);
      setPage(targetPage);
    } catch (e: any) {
      setError(e.message);
    }
  }

  onMount(() => { loadSessions(); });

  createEffect(on(() => props.organizationId, () => {
    setPage(1);
    setStatus("active");
    loadSessions(1);
  }));

  async function doRevoke(id: string, alsoRevoke: boolean) {
    try {
      await api.sessions.authRevoke(id, alsoRevoke);
      setMessage(alsoRevoke ? "Session & refresh token revoked" : "Session revoked");
      setTimeout(() => setMessage(""), 3000);
      loadSessions();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <section>
      <Show when={error()}>
        <div class="mb-3 bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-start justify-between">
          <pre class="whitespace-pre-wrap text-xs font-mono">{error()}</pre>
          <button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 shrink-0 cursor-pointer">✕</button>
        </div>
      </Show>
      <Show when={message()}>
        <div class="mb-3 bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg text-sm">{message()}</div>
      </Show>

      <div class="flex items-center justify-between gap-3 mb-4">
        <h2 class="text-lg font-semibold">
          {status() === "active" ? "Active Sessions" : status() === "expired" ? "Expired Sessions" : "All Sessions"} ({total()})
        </h2>
        <div class="flex items-center gap-1 rounded border border-gray-700 bg-gray-900 p-0.5">
          {(["active", "expired", "all"] as const).map((s) => (
            <button
              onClick={() => { setStatus(s); loadSessions(1); }}
              class={`px-3 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${status() === s ? "bg-gray-700 text-gray-200" : "text-gray-500 hover:text-gray-300"}`}
            >
              {s === "active" ? "Active" : s === "expired" ? "Expired" : "All"}
            </button>
          ))}
        </div>
      </div>

      <Show
        when={sessions().length > 0}
        fallback={<div class="text-gray-600 text-sm bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">No sessions found</div>}
      >
        <div class="border border-gray-800 rounded-lg overflow-x-auto">
          <div class="max-h-96 overflow-y-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-900 text-gray-400 sticky top-0">
                <tr>
                  <th class="text-left px-4 py-3 font-medium">User</th>
                  <th class="text-left px-4 py-3 font-medium">Role</th>
                  <th class="text-left px-4 py-3 font-medium">Status</th>
                  <th class="text-left px-4 py-3 font-medium">Refresh Token</th>
                  <th class="text-left px-4 py-3 font-medium">Created</th>
                  <th class="text-left px-4 py-3 font-medium">Expires</th>
                  <th class="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <For each={sessions()}>
                  {(s) => {
                    const expired = new Date(s.expiresAt) <= new Date();
                    return (
                    <tr class="hover:bg-gray-900/50">
                      <td class="px-4 py-3 text-gray-200">{s.userEmail}</td>
                      <td class="px-4 py-3 text-xs"><span class="px-2 py-0.5 rounded border border-purple-700 bg-purple-900/40 text-purple-300">{s.userRole}</span></td>
                      <td class="px-4 py-3 text-xs">
                        {expired
                          ? <span class="px-2 py-0.5 rounded border border-red-700 bg-red-900/40 text-red-300">Expired</span>
                          : <span class="px-2 py-0.5 rounded border border-emerald-700 bg-emerald-900/40 text-emerald-300">Active</span>
                        }
                      </td>
                      <td class="px-4 py-3 text-xs">
                        {s.refreshTokenId
                          ? <span class="px-2 py-0.5 rounded border border-emerald-700 bg-emerald-900/40 text-emerald-300">linked</span>
                          : <span class="text-gray-600">—</span>
                        }
                      </td>
                      <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                      <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.expiresAt).toLocaleString()}</td>
                      <td class="px-4 py-3 text-right">
                        {expired
                          ? <span class="text-xs text-gray-600">—</span>
                          : <button onClick={() => setRevokeConfirm({ id: s.id, hasRefreshToken: !!s.refreshTokenId })} class="text-xs px-2 py-1 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                        }
                      </td>
                    </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          page={page()}
          total={totalPages()}
          onChange={(p) => { loadSessions(p); }}
        />
      </Show>

      <Show when={revokeConfirm()}>
        {(confirm) => (
          <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div class="w-full max-w-sm rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
              <div class="px-5 py-4">
                <p class="text-sm text-gray-200">Revoke this session?</p>
                {confirm().hasRefreshToken && (
                  <p class="mt-1 text-xs text-gray-400">
                    This session has a linked refresh token. You can also revoke it to prevent the user from obtaining a new session.
                  </p>
                )}
              </div>
              <div class="flex border-t border-gray-800">
                <button
                  onClick={() => { doRevoke(confirm().id, false); setRevokeConfirm(null); }}
                  class="flex-1 px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 rounded-bl-xl cursor-pointer"
                >
                  Session only
                </button>
                {confirm().hasRefreshToken && (
                  <button
                    onClick={() => { doRevoke(confirm().id, true); setRevokeConfirm(null); }}
                    class="flex-1 px-4 py-3 text-sm text-red-400 hover:bg-gray-800 border-l border-gray-800 cursor-pointer"
                  >
                    Session + RT
                  </button>
                )}
                <button
                  onClick={() => setRevokeConfirm(null)}
                  class="px-4 py-3 text-sm text-gray-500 hover:bg-gray-800 rounded-br-xl border-l border-gray-800 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </Show>
    </section>
  );
}

function Pagination(props: { page: number; total: number; onChange: (p: number) => void }) {
  return (
    <div class="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-800 bg-gray-900/50">
      <button onClick={() => props.onChange(Math.max(1, props.page - 1))} disabled={props.page === 1} class="px-3 py-1 text-xs bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Prev</button>
      <span class="text-xs text-gray-400">Page {props.page} of {props.total}</span>
      <button onClick={() => props.onChange(Math.min(props.total, props.page + 1))} disabled={props.page >= props.total} class="px-3 py-1 text-xs bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Next</button>
    </div>
  );
}
